import { TableName, TableRow } from "#/types/supabase";
import React, { useEffect, useState } from "react";
import { uuid } from "#/utils/uuid";
import { OperationType, RealtimeContext, RealtimeEnabledTable } from "#next/hooks/with-react-context/use-realtime-subscription/realtime-provider";

export interface UpdateRealtimePayload<T extends TableName> {
  old: TableRow<T>,
  new: TableRow<T>
}

/**
 *  For "Delete", old should only be an object with all the properties when the table
 *  in question ends with "mapping", otherwise it will be just the id of the deleted table.
 *  This is because enabling the feature of emitting all the data for a deleted table is
 *  apparently quite expensive, so we only enable it for mapping tables, where the consumer
 *  of the delete event can actually query the records which it used to map to, to do something
 *  with it.
 *
 */

export interface DeleteRealtimePayload<T extends TableName> {
  old: T extends `${string}_mapping` ? TableRow<T> : Pick<TableRow<T>, "id">
}

export interface InsertRealtimePayload<T extends TableName> {
  new: TableRow<T>
}

export type RealtimePayload<
  T extends TableName,
  O extends OperationType
> = {
  Update: UpdateRealtimePayload<T>,
  Delete: DeleteRealtimePayload<T>,
  Insert: InsertRealtimePayload<T>
}[O];

export type RealtimeUpdateFn<
  T extends TableName,
  K extends OperationType
> = (
  payload: RealtimePayload<T, K>
) => (Promise<void> | void);

export interface UseRealtimeSubscription<T extends TableName> {
  table: T,
  onUpdate?: RealtimeUpdateFn<T, "Update">,
  onDelete?: RealtimeUpdateFn<T, "Delete">,
  onInsert?: RealtimeUpdateFn<T, "Insert">,
  condition?: boolean,
  logRealtime?: boolean
}

/**
 *  Hook which allows components to subscribe to real-time database changes.
 *  @example
 *   useRealtimeSubscription({
 *     table: "tenant",
 *     onUpdate: payload => {
 *        // Do something with payload.old or payload.new when a record in the tenant table is updated
 *     },
 *     onDelete: payload => {
 *        // Do something with payload.old when a record in the tenant table is deleted
 *        // If the table name ends with mapping (for e.g. tenant_to_profile_mapping), payload.old will be the
 *        //  full record, otherwise (for e.g. tenant), it will be just the id of the record. (See the
 *        //  comment above the definition for the type "DeleteRealtimePayload" above for more information.)
 *     },
 *     onInsert: payload => {
 *       // Do something with payload.new when a record in the tenant table is inserted
 *     }
 *   });
 *
 */
export const useRealtimeSubscription = <T extends RealtimeEnabledTable>(args: UseRealtimeSubscription<T>): void => {
  if (!args.onUpdate && !args.onDelete && !args.onInsert) {
    throw new Error("Must provide at least one of onUpdate, onDelete, or onInsert");
  }
  const useRealtimeContext = React.useContext(RealtimeContext);
  /**
   *  1. We need to check for existing channels already subscribed to
   *  2. If one exists, add the handlers to it
   *  3. If one doesn't exist, create a new subscription
   *  4. On cleanup, remove handler
   */
  const [ instanceId ] = useState(uuid());
  const [ isSubscribed, setIsSubscribed ] = useState(false);

  useEffect(() => {
    if (isSubscribed || (args.condition !== undefined && !args.condition)) {
      return;
    }
    setIsSubscribed(true);
    useRealtimeContext.subscribeToChannel(instanceId, args);

  }, [ isSubscribed, args, useRealtimeContext, instanceId, args.condition ]);

  /** Unsubscribe from real-time updates when the component calling this (and thus this hook) unmounts. */
  useEffect(() => {
    return (): void => {
      if (isSubscribed) {
        useRealtimeContext.unsubscribeFromChannel(instanceId, args);
        setIsSubscribed(false);
      }
    };
  }, []);

};